import React from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { compose, Dispatch } from 'redux';
import StateInterface from '../../../redux/types';
import { hideOverlay as hideOverlayAction } from '../../../redux/overlays/actions';
import { OverlayId } from '../types';
import Flyout from '../GenericFlyout';
import withConfig from '../../../hoc/withConfig';
import { Config } from '../../../../common/types/config';
import useTranslator from '../../../hooks/useTranslator';
import useMenuItemChecks, { menuCheckKey } from '../../../hooks/useMenuItemCheck';
import { phoneNumberToLink } from '../../../../common/helpers/phoneNumber';
import { VictorQaClasses } from '../../../../common/qaIds';
import NavSection from './NavSection';
import NavListItem from './NavItem';

export const OVERLAY_ID = OverlayId.ACCOUNT_FLYOUT;

export interface StaticNavItem {
    title: string;
    url: string;
    className?: string;
    checkKey?: menuCheckKey;
}

export interface StaticNavSection {
    key: string;
    title: string;
    items: StaticNavItem[];
}

const accountMenuItems = (config: Config, isLoggedIn: boolean): StaticNavSection[] => [
    {
        key: 'login',
        title: '',
        items: isLoggedIn
            ? [{ title: 'Logout', url: config.links.logout }]
            : [{ title: 'Login', url: config.links.login }],
    },
    {
        key: 'outside',
        title: '',
        items: [
            { title: 'Home', url: config.links.home },
            { title: 'Shop the look', url: config.links.looks },
            { title: 'Inspiration', url: config.links.olapicUgcGallery, checkKey: menuCheckKey.OlapicUgcGallery },
        ],
    },
    {
        key: 'account',
        title: 'My account',
        items: [
            { title: 'Account overview', url: config.links.myAccount, className: VictorQaClasses.accountMenuOverview },
            { title: 'My Wishlist', url: config.links.wishList },
            { title: 'My Moodboard', url: config.links.moodboard, checkKey: menuCheckKey.Moodboard },
            { title: 'My orders', url: config.links.myOrders },
            { title: 'reviews_Product review', url: config.links.myReviews },
            { title: 'My Vouchers', url: config.links.myCoupons },
            { title: 'My credentials', url: config.links.myEditAccount },
            { title: 'My Addresses', url: config.links.myAddress },
            { title: 'My newsletter', url: config.links.myNewsletter },
            { title: 'My notifications', url: config.links.myNotifications },
        ],
    },
    {
        key: 'CustomerService',
        title: 'Customer Service',
        items: [
            { title: 'Hotline', url: phoneNumberToLink(config.hotline) },
            { title: 'Contact', url: config.links.respondentContact },
        ],
    },
];

interface AccountFlyoutProps {
    isLoggedIn: boolean;
    config: Config;
    hideOverlay: typeof hideOverlayAction;
}

const StyledContainer = styled.div`
    padding: 10px 30px 90px;
`;

export const AccountFlyout: React.SFC<AccountFlyoutProps> = ({ isLoggedIn, config }) => {
    const itemChecks = useMenuItemChecks(config);
    const t = useTranslator();
    const createNavSection = (navLisItems: StaticNavItem[]) =>
        navLisItems
            .filter(({ checkKey }) => !checkKey || !!itemChecks[checkKey])
            .map(navListItem => (
                <NavListItem
                    className={navListItem.className}
                    key={navListItem.title}
                    title={t(navListItem.title)}
                    url={navListItem.url}
                />
            ));

    const createNavigation = () =>
        accountMenuItems(config, isLoggedIn).map(navList => (
            <NavSection key={navList.key} title={t(navList.title)}>
                {createNavSection(navList.items)}
            </NavSection>
        ));

    return (
        <Flyout identifier={OVERLAY_ID}>
            <StyledContainer>{createNavigation()}</StyledContainer>
        </Flyout>
    );
};

const mapStateToProps = (state: StateInterface) => {
    const { isLoggedIn } = state.login;
    return { isLoggedIn };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
    hideOverlay: (id: string) => dispatch(hideOverlayAction(id)),
});

export default compose(connect(mapStateToProps, mapDispatchToProps), withConfig)(AccountFlyout);
